<template>
  <div class="app-page">
    <Header ref="header">
      <template #header-slot>
        <BreadcrumbNavigation />
      </template>
    </Header>
    <div class="main-page-div">
      <main class="main-display-area">
        <div class="dataset-history-page">
          <div class="page-header">
            <div class="header-top">
              <IconButton
                v-tooltip="'Back to Datasets'"
                class="back-btn"
                :icon="'chevron_left'"
                :width="24"
                :height="24"
                @click="goBack"
              />
              <h1>{{ dataset?.name || 'Dataset History' }}</h1>
            </div>
            <h2 v-if="dataset?.description">{{ dataset.description }}</h2>
            <hr>
          </div>
          
          <div class="history-container">
            <div class="version-section">
              <div class="section-header">
                <h3>Version History</h3>
                <button class="button toggle-btn" @click="toggleVersionHistory">
                  {{ showVersionHistory ? 'Hide' : 'Show' }}
                </button>
              </div>
              <div v-if="showVersionHistory" class="version-wrapper">
                <DatasetVersions 
                  v-if="dataset?.id"
                  :datasetId="dataset.id"
                />
              </div>
            </div>
            
            <div class="changelog-section">
              <div class="section-header">
                <h3>Change Log</h3>
                <button class="button toggle-btn" @click="toggleChangeLogs">
                  {{ showChangeLogs ? 'Hide' : 'Show' }}
                </button>
              </div>
              <div v-if="showChangeLogs" class="changelog-wrapper">
                <DatasetChangeLogs
                  v-if="dataset?.id"
                  :datasetId="dataset.id"
                />
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useStore } from 'vuex'
import Header from '@/components/Header.vue'
import BreadcrumbNavigation from '@/components/BreadcrumbNavigation.vue'
import IconButton from '@/components/IconButton.vue'
import DatasetVersions from '@/components/DatasetComponent/DatasetManagement/DatasetVersions.vue'
import DatasetChangeLogs from '@/components/DatasetComponent/DatasetManagement/DatasetChangeLogs.vue'
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface'

const router = useRouter()
const route = useRoute()
const store = useStore()

const dataset = ref(null)
const loading = ref(true)
const showVersionHistory = ref(true)
const showChangeLogs = ref(false)

const datasetId = computed(() => parseInt(route.params.id))

onMounted(async () => {
  await loadDataset()
})

async function loadDataset() {
  try {
    const dsConn = new DatastoreConnect(store.state.enterpriseServerUrl)
    const response = await dsConn.api('dataset.get', { dataset_id: datasetId.value })
    if (response?.result) {
      dataset.value = response.result
    }
  } catch (error) {
    console.error('Error loading dataset:', error)
  } finally {
    loading.value = false
  }
}

function goBack() {
  router.back()
}

function toggleVersionHistory() {
  showVersionHistory.value = !showVersionHistory.value
}

function toggleChangeLogs() {
  showChangeLogs.value = !showChangeLogs.value
}
</script>

<style lang="scss" scoped>
.app-page {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.main-page-div {
  display: flex;
  flex-direction: row;
  flex: 1;
  overflow: hidden;
}

.main-display-area {
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: auto;
}

.dataset-history-page {
  display: flex;
  flex-direction: column;
  min-height: 100%;
  padding: 24px;
  background-color: white;
  height: 100vh;
    overflow-y: scroll;
}

.page-header {
  display: flex;
  flex-direction: column;
  margin-bottom: 24px;
  flex-shrink: 0;
  
  .header-top {
    display: flex;
    align-items: center;
    gap: 16px;
  }
  
  h1 {
    font-size: 28px;
    font-weight: 600;
    margin: 0;
  }
  
  h2 {
    font-size: 16px;
    font-weight: 400;
    margin: 0 0 12px 0;
    @include themify() {
      color: themed('body-text-color-secondary');
    }
  }
  
  hr {
    width: 100%;
    border: none;
    border-top: 1px solid rgba(0, 0, 0, 0.1);
    margin: 0 0 16px 0;
  }
}

.back-btn {
  @include themify() {
    color: themed('icon-color-primary');
  }
}

.history-container {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.version-section {
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  margin-bottom: 20px;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
  
  h3 {
    font-size: 18px;
    font-weight: 600;
    margin: 0;
  }
}

.toggle-btn {
  padding: 6px 16px;
  font-size: 13px;
  min-width: 60px;
}

.version-wrapper {
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 4px;
}

.show-changelog-btn {
  align-self: flex-start;
  margin: 0;
  flex-shrink: 0;
}

.changelog-section {
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
}

.changelog-wrapper {
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 4px;
}
</style>
